"use client";

import { gql, useMutation } from "@apollo/client";
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { useCallback, useState } from "react";
import { useWidgetInstance } from "./widget-instance-context";

/**
 * Visitor passkey (WebAuthn) sign-in — the public-site counterpart to
 * the host-token / Google flows in {@link useVisitorAuth}.
 *
 * **Why this lives outside `useVisitorAuth`.** Passkey is a TWO-STEP
 * ceremony (options → browser prompt → verify), not a single identity
 * token, and the BE's verify mutation already returns a full visitor
 * session. So we run the whole ceremony here and, on success, stamp the
 * returned token into Apollo's auth slot (`WidgetInstance.token`) and
 * hand the session up to the shell — which adopts it as a verified
 * session override. (The renewal story matches Google's: the proof is
 * spent once; a silent re-handshake can't replay a WebAuthn assertion,
 * so the session degrades to anonymous on expiry until the visitor
 * signs in again. Tracked as renewal hardening in WEB_AUTHENTICATION.md.)
 *
 * **GraphQL inline, not codegen.** The four passkey mutations are
 * declared with `gql` here rather than in `*.graphql` so they don't
 * require a codegen run (which needs a live BE) to ship — the rest of
 * the widget's typed operations stay codegen-driven.
 *
 * **RP = the embedding origin.** We pass `window.location.origin`; the
 * BE validates it against the integration's allow-list and derives the
 * RP id from it. Works in the in-page embeds (web-component /
 * react-component share the host origin); the cross-origin iframe embed
 * needs the host to delegate the `publickey-credentials-*` permission
 * policy (see WEB_AUTHENTICATION.md).
 */

/** The visitor session a verified passkey ceremony yields — same shape
 *  the host-token / Google handshakes return. */
export interface PasskeySessionResult {
  token: string;
  expiresIn: number;
  chatId: string;
  peopleId: string;
  displayName: string;
  /** `'identified'` on success — drives the shell's `verified` UI. */
  kind: string;
}

// Exported for the hook's unit test (MockedProvider matches on the
// parsed document); not part of the public API otherwise.
export const PASSKEY_REG_OPTIONS = gql`
  mutation VisitorPasskeyRegistrationOptions($input: VisitorPasskeyOptionsInput!) {
    visitorPasskeyRegistrationOptions(input: $input) {
      sessionId
      optionsJson
    }
  }
`;

export const PASSKEY_REG_VERIFY = gql`
  mutation VerifyVisitorPasskeyRegistration($input: VisitorPasskeyVerifyInput!) {
    verifyVisitorPasskeyRegistration(input: $input) {
      token
      expiresIn
      chatId
      peopleId
      kind
      displayName
      shortHandle
    }
  }
`;

export const PASSKEY_AUTH_OPTIONS = gql`
  mutation VisitorPasskeyAuthenticationOptions($input: VisitorPasskeyOptionsInput!) {
    visitorPasskeyAuthenticationOptions(input: $input) {
      sessionId
      optionsJson
    }
  }
`;

export const PASSKEY_AUTH_VERIFY = gql`
  mutation VerifyVisitorPasskeyAuthentication($input: VisitorPasskeyVerifyInput!) {
    verifyVisitorPasskeyAuthentication(input: $input) {
      token
      expiresIn
      chatId
      peopleId
      kind
      displayName
      shortHandle
    }
  }
`;

type OptionsResult = { sessionId: string; optionsJson: string };

export type PasskeyStatus = "idle" | "busy" | "error";

export interface UsePasskeyAuth {
  /** Sign in with an EXISTING passkey (returning visitor / new device). */
  authenticate: () => Promise<PasskeySessionResult | null>;
  /** Create a NEW passkey (first-time visitor on this site). */
  register: () => Promise<PasskeySessionResult | null>;
  status: PasskeyStatus;
  error: string | null;
  /** `false` when the browser has no WebAuthn — hide the buttons. */
  supported: boolean;
}

export function usePasskeyAuth(opts: {
  visitorId: string;
  /** Embedding page origin — `window.location.origin`. */
  origin: string;
}): UsePasskeyAuth {
  const instance = useWidgetInstance();
  const [status, setStatus] = useState<PasskeyStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const [regOptions] = useMutation<{ visitorPasskeyRegistrationOptions: OptionsResult }>(
    PASSKEY_REG_OPTIONS,
  );
  const [regVerify] = useMutation<{ verifyVisitorPasskeyRegistration: PasskeySessionResult }>(
    PASSKEY_REG_VERIFY,
  );
  const [authOptions] = useMutation<{ visitorPasskeyAuthenticationOptions: OptionsResult }>(
    PASSKEY_AUTH_OPTIONS,
  );
  const [authVerify] = useMutation<{ verifyVisitorPasskeyAuthentication: PasskeySessionResult }>(
    PASSKEY_AUTH_VERIFY,
  );

  // Shared ceremony runner: fetch options → drive the browser prompt →
  // verify. Both register + authenticate funnel through here; only the
  // option/verify mutations + the `startX` browser call differ.
  const run = useCallback(
    async (kind: "register" | "authenticate"): Promise<PasskeySessionResult | null> => {
      setStatus("busy");
      setError(null);
      try {
        const input = { visitorId: opts.visitorId, origin: opts.origin };
        // 1. Options + challenge from the BE.
        const opt =
          kind === "register"
            ? (await regOptions({ variables: { input } })).data?.visitorPasskeyRegistrationOptions
            : (await authOptions({ variables: { input } })).data
                ?.visitorPasskeyAuthenticationOptions;
        if (!opt) throw new Error("passkey_options_failed");

        const options = JSON.parse(opt.optionsJson);

        // 2. Browser prompt (Touch ID / Face ID / security key). Throws
        //    on user-cancel or no-credential — surfaced as `error`.
        const response =
          kind === "register"
            ? await startRegistration(options)
            : await startAuthentication(options);

        // 3. Verify the signed response → a full visitor session.
        const verifyInput = {
          visitorId: opts.visitorId,
          sessionId: opt.sessionId,
          responseJson: JSON.stringify(response),
        };
        const session =
          kind === "register"
            ? (await regVerify({ variables: { input: verifyInput } })).data
                ?.verifyVisitorPasskeyRegistration
            : (await authVerify({ variables: { input: verifyInput } })).data
                ?.verifyVisitorPasskeyAuthentication;
        if (!session) throw new Error("passkey_verify_failed");

        // Stamp the token so every subsequent Apollo call is authed as
        // the now-verified visitor; the shell adopts the rest for UI.
        instance.token = session.token;
        setStatus("idle");
        return session;
      } catch (err) {
        setStatus("error");
        setError((err as Error).message || "passkey_failed");
        return null;
      }
    },
    [opts.visitorId, opts.origin, regOptions, regVerify, authOptions, authVerify, instance],
  );

  const authenticate = useCallback(() => run("authenticate"), [run]);
  const register = useCallback(() => run("register"), [run]);

  return {
    authenticate,
    register,
    status,
    error,
    supported: typeof window !== "undefined" && browserSupportsWebAuthn(),
  };
}
